import { useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { conf } from "../../utils/config";
import { useSelector } from "react-redux";
import {
  selectStudent,
  selectToken,
  setListadoCarreras,
  setStudentMostrarHorarios,
  setDebeAlertarSuperposicion,
  setStudentBloqueoActivo,
  setStudentBloqueoMensajes,
  setToken,
  setCarrearActual,
  setStudentInicializado,
} from "../../reduxSlices/studentSlice";
import { useAppDispatch } from "../../app/store";
import { selectParameter } from "../../reduxSlices/parametersSlice";
import { AppState } from "../../enums/appState.enum";
import { clearAutoLogout, getRedirectUri, shouldSkipUnauthorizedLogout } from "../../utils/authHelpers";
import { setServerError } from "../../reduxSlices/parametersSlice";
import { setIsLoggingOut, setIsLogginProcess } from "../../reduxSlices/globalFlagsSlice";
import { axiosInstances } from "./AxiosInstances";

export enum ServerStatus {
  Success = 200,
  BadRequest = 400,
  Unauthorized = 401,
  InternalServerError = 500,
}

export default function AxiosManager() {
  const token = useSelector(selectToken);
  const student = useSelector(selectStudent);
  const dispatch = useAppDispatch();
  const redirectUri = getRedirectUri();
  const BASE_URL = conf("BASE_URL");
  const CLONE_URL = conf("CLONE_URL");
  const cantidadDeUrls = conf("CANTIDAD_CLONES");

  // Configurar axios por defecto con la URL base
  if (BASE_URL) {
    axios.defaults.baseURL = BASE_URL;
  }

  // Configurar headers por defecto
  axios.defaults.headers.common["Accept"] = "application/json";
  axios.defaults.headers.common["Content-Type"] = "application/json";

  const { instance } = useMsal();
  const { started, appState } = useSelector(selectParameter);

  const isLoggingOut = useRef(false);

  // Función para configurar el token de manera robusta
  const configureToken = (config: any) => {
    const storedToken = localStorage.getItem("token");
    const reduxToken = token;

    if (storedToken === "x") {
      console.log("🚪 Token inválido detectado, iniciando logout...");
      handleLogout(appState);
      return config;
    }

    // Usar el token de Redux si está disponible, sino el del localStorage
    const authToken = reduxToken?.length > 0 ? reduxToken : storedToken;

    if (authToken && authToken !== "x") {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${authToken}`,
      };
    } else {
      console.warn("⚠️ No se encontró token válido para la solicitud:", config.url);
    }

    return config;
  };

  const handleLogout = async (state: string) => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    clearAutoLogout();

    try {
      const redirectUri = getRedirectUri();
      const isCampus = !!localStorage.getItem("campusToken");

      dispatch(setIsLoggingOut(true));
      localStorage.clear();
      sessionStorage.clear();

      // Limpiar estado de Redux
      dispatch(setToken(""));
      dispatch(setCarrearActual(""));
      dispatch(setListadoCarreras([]));
      dispatch(setStudentInicializado(0));
      dispatch(setStudentMostrarHorarios(false));
      dispatch(setStudentBloqueoActivo(false));
      dispatch(setStudentBloqueoMensajes({ mensaje: "", subMensaje: "" }));
      dispatch(setDebeAlertarSuperposicion(false));
      dispatch(setIsLogginProcess(false));
      if (state !== AppState.BY_PASS) {
        await instance.logoutRedirect({
          postLogoutRedirectUri: redirectUri,
        });
      }
    } finally {
      isLoggingOut.current = false;
    }
  };

  const getBaseURL = (idPersona: number) => {
    const cantidadDeClones = Number.parseInt(cantidadDeUrls);
    if (cantidadDeClones > 1) {
      if (!idPersona) {
        console.error("❌ No se encontró idPersona para la solicitud");
        console.log("🔍 URL por defecto:", BASE_URL);
        return BASE_URL;
      }
      const instancia = Math.floor(Math.random() * 20);
      const cloneUrl = CLONE_URL.replace("_XXX_", instancia.toString());
      console.log("🔍 URL asignada:", cloneUrl);
      return cloneUrl;
    } else {
      return BASE_URL;
    }
  };

  // Configurar interceptores para las instancias de axiosInstances
  useEffect(() => {
    const instanceInterceptors: Array<{
      instance: AxiosInstance;
      requestId: number;
      responseId: number | null;
    }> = [];

    // Configurar interceptores para cada instancia
    Object.entries(axiosInstances).forEach(([key, instance]) => {
      // Obtener la URL de fallback configurada en la instancia
      const fallbackBaseURL = (instance as any)._fallbackBaseURL;
      const skipFallback = (instance as any)._skipFallback;

      // Interceptor de request: token + headers fijos (CORS/Cloudflare no se desconfiguren tras un error)
      const requestId = instance.interceptors.request.use(
        (config) => {
          (config as any)._fallbackUsed = false;
          const h = config.headers as Record<string, unknown>;
          if (h) {
            h.Accept = h.Accept ?? "application/json";
            h["Content-Type"] = h["Content-Type"] ?? "application/json";
          }
          return configureToken(config);
        },
        (error) => {
          console.error(`❌ Error en interceptor de request de ${key}:`, error);
          return Promise.reject(error);
        }
      );

      // Interceptor de response para manejar fallback
      // Configurar siempre el interceptor de response para el fallback, independientemente de 'started'
      let responseId: number | null = null;
      responseId = instance.interceptors.response.use(
        (response) => {
          // Solo aplicar lógica de logout/errores si 'started' es true
          if (started) {
            if (
              response.status === ServerStatus.Unauthorized &&
              !shouldSkipUnauthorizedLogout({ response }, response.config?.url)
            ) {
              handleLogout(appState);
            }
            if (response.status === ServerStatus.InternalServerError) {
              dispatch(setServerError(true));
            }
          }
          return response;
        },
        async (error: AxiosError) => {
          const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _fallbackUsed?: boolean };

          // Si no hay config, no podemos hacer fallback
          if (!originalRequest) {
            console.error(`❌ Error sin config en ${key}:`, error);
            return Promise.reject(error);
          }

          // Manejar errores de autenticación (solo si started es true).
          // No forzar logout en doLogin/doCampusLogin ni cuando el IPO no está en whitelist
          // (el usuario debe poder elegir otra carrera / ver el toast específico).
          if (started && error.response?.status === ServerStatus.Unauthorized) {
            if (!shouldSkipUnauthorizedLogout(error, originalRequest.url)) {
              handleLogout(appState);
            }
            return Promise.reject(error);
          }

          if (started && error.response?.status === ServerStatus.InternalServerError && !error.config?.url?.includes("prioridad")) {
            dispatch(setServerError(true));
          }

          // Determinar si debemos hacer fallback
          // Hacer fallback solo si:
          // 1. No se ha usado fallback antes
          // 2. La instancia no está configurada para saltar el fallback
          // 3. Hay una URL de fallback configurada
          // 4. El error es 404 (recurso no encontrado)
          const isNotFound = error.response?.status === 404;

          const shouldFallback =
            !originalRequest._fallbackUsed &&
            !skipFallback &&
            fallbackBaseURL &&
            isNotFound;

          if (shouldFallback) {
            originalRequest._fallbackUsed = true;
            console.log(`🔄 Fallback (404): ${key} - recurso no encontrado, intentando con fallback URL...`, {
              originalBaseURL: originalRequest.baseURL,
              fallbackBaseURL: fallbackBaseURL,
              url: originalRequest.url,
              errorStatus: error.response?.status,
              errorMessage: error.message,
            });

            try {
              // Crear nueva configuración con la URL de fallback (sin usar axios global
              // para no pisar baseURL ni depender de sus interceptores y evitar CORS en requests siguientes)
              const fallbackConfig: InternalAxiosRequestConfig = {
                ...originalRequest,
                baseURL: fallbackBaseURL,
              };
              const fh = fallbackConfig.headers as Record<string, unknown>;
              if (fh) {
                fh.Accept = "application/json";
                fh["Content-Type"] = "application/json";
              }

              // Aplicar token al fallback
              const configWithToken = configureToken(fallbackConfig);

              // Usar la MISMA instancia (no axios global) para no alterar estado ni interceptores globales
              const response = await instance.request(configWithToken);
              console.log(`✅ Fallback exitoso para ${key} con ${fallbackBaseURL}`);
              return response;
            } catch (fallbackError) {
              console.error(`❌ Fallback también falló para ${key}:`, fallbackError);
              return Promise.reject(fallbackError);
            }
          }

          return Promise.reject(error);
        }
      );

      instanceInterceptors.push({ instance, requestId, responseId });
    });

    return () => {
      console.log("🧹 Limpiando interceptores de instancias de axios...");
      instanceInterceptors.forEach(({ instance, requestId, responseId }) => {
        instance.interceptors.request.eject(requestId);
        if (responseId !== null) {
          instance.interceptors.response.eject(responseId);
        }
      });
    };
  }, [token, appState, started, dispatch, BASE_URL]);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Usar getBaseURL si student.id tiene un valor, sino usar BASE_URL por defecto
        if (student?.id) {
          const assignedBaseURL = getBaseURL(student.id);
          config.baseURL = assignedBaseURL;
          console.log("🌐 Usando URL base asignada para student.id:", student.id, "URL:", assignedBaseURL);
        } else {
          console.log("🔍 No se encontró idPersona para la solicitud");
          const BASE_URL = conf("BASE_URL");
          if (BASE_URL) {
            config.baseURL = BASE_URL;
          }
        }
        // Configurar el token de manera robusta
        return configureToken(config);
      },
      (error) => {
        console.error("❌ Error en interceptor de request:", error);
        return Promise.reject(error);
      }
    );

    let responseInterceptor: number | null = null;
    if (started) {
      responseInterceptor = axios.interceptors.response.use(
        (response) => {
          if (
            response.status === ServerStatus.Unauthorized &&
            !shouldSkipUnauthorizedLogout({ response }, response.config?.url)
          ) {
            handleLogout(appState);
          }
          if (response.status === ServerStatus.InternalServerError) {
            dispatch(setServerError(true));
          }
          return response;
        },
        (error: AxiosError) => {
          if (error.response?.status === ServerStatus.Unauthorized) {
            if (!shouldSkipUnauthorizedLogout(error, error.config?.url)) {
              handleLogout(appState);
            }
          }
          if (error.response?.status === ServerStatus.InternalServerError) {
            dispatch(setServerError(true));
          }
          return Promise.reject(error);
        }
      );
    }

    return () => {
      console.log("🧹 Limpiando interceptor de axios...");
      axios.interceptors.request.eject(requestInterceptor);
      if (responseInterceptor !== null) {
        axios.interceptors.response.eject(responseInterceptor);
      }
    };
  }, [token, appState, started, dispatch, student]);

  return <></>;
}
